import { useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";
import Main from "../../components/Communications/Main";
import {
  useCommunication,
  useDownloadCommunicationAttachments,
  useTrakReadedCommunication,
} from "../../hooks/communications.hooks";
import { useScreenConfig } from "../../hooks/useScreenConfig";
import { useStores } from "../../hooks/useStores";
import { Attachment, Screens } from "../../types";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import api from "../../services/api";

export const CommunicationDetail: React.FC = () => {
  const { session } = useStores();
  const params = useParams();
  const config = useScreenConfig(Screens.COMMUNICATIONS);
  const { t } = useTranslation();

  const { isFetching, data: communication = null } = useCommunication(
    Number(params.communicationId) || -1
  );

  const { refetch: downloadCommunicationAttachments } =
    useDownloadCommunicationAttachments(Number(params.communicationId));

  const { isLoading: isSubmitting, mutateAsync: track } =
    useTrakReadedCommunication(
      session.user?.Id || -1,
      session.activeCompanyId || -1
    );

  useEffect(() => {
    if (communication && communication.Unreaded && !session.user?.Internal) {
      track(communication.Id);
    }
  }, [communication, session.user]);

  const handleDownloadAttachments = useCallback(async () => {
    const response = await downloadCommunicationAttachments();
    const blob = response.data;

    if (blob) {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${communication?.Subject || "allegati"}.zip`
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
      return true;
    }

    throw new Error("error");
  }, [communication?.Subject, downloadCommunicationAttachments]);

  const onDownloadAttachments = useCallback(() => {
    toast.promise(
      handleDownloadAttachments(),
      {
        pending: {
          render: t("components.sharepoint.downloadingAttachments"),
          type: "default",
        },
        success: {
          render: t("components.sharepoint.downloadCompleted"),
          type: "success",
        },
        error: {
          render: t("components.sharepoint.downloadAttachmentsFailed"),
          type: "error",
        },
      }
    );
  }, [handleDownloadAttachments, t]);

  const onAttachmentClick = useCallback(
    (attachment: Attachment) => {
      const userId = session.user?.Id;
      const companyId = session.activeCompanyId;
      if (!userId || !companyId || !communication) return;

      const downloadAttachment = async () => {
        const blob = await api.downloadCommunicationAttachment(
          userId,
          companyId,
          communication.Id,
          attachment.Id
        );

        if (blob) {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", attachment.OriginalFileName);
          document.body.appendChild(link);
          link.click();
          link.parentNode?.removeChild(link);
          window.URL.revokeObjectURL(url);
          return true;
        }

        throw new Error("Download failed");
      };

      toast.promise(downloadAttachment(), {
        pending: {
          render: t("components.sharepoint.downloadingAttachment"),
          type: "default",
        },
        success: {
          render: t("components.sharepoint.downloadAttachmentCompleted"),
          type: "success",
        },
        error: {
          render: t("components.sharepoint.downloadAttachmentFailed"),
          type: "error",
        },
      });
    },
    [session.user, session.activeCompanyId, communication, t]
  );

  return communication ? (
    <Main
      communication={communication}
      config={config}
      onDownloadAttachments={onDownloadAttachments}
      onAttachmentClick={onAttachmentClick}
    />
  ) : null;
};
